import { action, computed, makeObservable, observable } from "mobx";
import { inject, injectable } from "tsyringe";
import {
  type IWarehouseApi,
  type ProductExpiryFilter,
  type ProductPlacementFilter,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { ViewModel } from "../../lib/vvm";
import { Measures } from "../../types/entities";
import { Product } from "../../stores/Product";
import { STORE_TOKEN, type IStore } from "../../stores/store";
import {
  NAVIGATION_STORE_TOKEN,
  type INavigationStore,
} from "../../stores/navigation";
import { normalizeTypeTag } from "../../utils/typeTags";
import {
  buildProductsListFilterChips,
  buildProductsListPageTitle,
  buildProductsListRequestKey,
  type ProductsListFilterChip,
  type ProductsListFilterState,
} from "./viewmodel/filters";
import {
  loadMoreProductsList,
  refreshProductsList,
  type ProductsListPagingState,
} from "./viewmodel/paging";
import {
  openAllProductsList,
  openExpiryFilter,
  openLocationFilter,
  openMeasureFilter,
  openPlacementFilter,
  openProductCard,
  openQueryFilter,
  openReferenceId,
  openTypeTagFilter,
  resolveProductLocation,
  type ProductsListNavigationState,
} from "./viewmodel/navigation";

export interface ProductsListPageViewProps {
  title?: string;
  placement?: ProductPlacementFilter;
  expiry?: ProductExpiryFilter;
  expiringDays?: number;
  measure?: "all" | Measures;
  q?: string;
  typeTag?: string;
}

@injectable()
export class ProductsListPageViewModel extends ViewModel<
  unknown,
  ProductsListPageViewProps
> {
  private readonly pageSize = 30;
  private activeRequestKey = "";

  @observable.shallow
  items: Product[] = [];

  @observable
  total = 0;

  @observable
  offset = 0;

  @observable
  isLoadingInitial = false;

  @observable
  isLoadingMore = false;

  @observable
  hasMore = true;

  @observable
  error: string | null = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();
    makeObservable(this);
  }

  @computed
  get pageTitle(): string {
    return buildProductsListPageTitle({
      explicitTitle: this.viewProps.title,
      filters: this.filterState,
    });
  }

  @computed
  get summaryLabel(): string {
    if (this.total <= 0) {
      return `${this.items.length} позиций`;
    }

    return `${this.items.length} из ${this.total}`;
  }

  @computed
  get isBusy(): boolean {
    return this.isLoadingInitial || this.isLoadingMore;
  }

  @computed
  get canAutoLoadMore(): boolean {
    return this.hasMore && !this.error;
  }

  @computed
  get placementFilter(): ProductPlacementFilter {
    return this.viewProps.placement || "all";
  }

  @computed
  get expiryFilter(): ProductExpiryFilter {
    return this.viewProps.expiry || "all";
  }

  @computed
  get measureFilter(): "all" | Measures {
    return this.viewProps.measure || "all";
  }

  @computed
  get expiringDays(): number {
    return this.viewProps.expiringDays ?? 7;
  }

  @computed
  get queryFilter(): string {
    return this.viewProps.q?.trim() || "";
  }

  @computed
  get typeTagFilter(): string {
    return normalizeTypeTag(this.viewProps.typeTag || "") || "";
  }

  @computed
  private get requestKey(): string {
    return buildProductsListRequestKey(this.filterState);
  }

  @computed
  get appliedFilterChips(): ProductsListFilterChip[] {
    return buildProductsListFilterChips({
      filters: this.filterState,
      openTypeTag: (tag) => this.openTypeTag(tag),
      openMeasureFilter: (measure) => this.openMeasureFilter(measure),
      openPlacementFilter: (placement) => this.openPlacementFilter(placement),
      openExpiryFilter: (expiry, expiringDays) =>
        this.openExpiryFilter(expiry, expiringDays),
      openQueryFilter: (query) => this.openQueryFilter(query),
      openAllProductsList: () => this.openAllProductsList(),
    });
  }

  protected onViewMounted(): void {
    this.activeRequestKey = this.requestKey;
    void this.refresh();
  }

  protected onViewUpdated(): void {
    if (this.requestKey === this.activeRequestKey) {
      return;
    }

    this.activeRequestKey = this.requestKey;
    void this.refresh();
  }

  @action.bound
  async refresh(): Promise<void> {
    await refreshProductsList({
      state: this.pagingState,
      loadMore: () => this.loadMore(),
    });
  }

  @action.bound
  async loadMore(): Promise<void> {
    await loadMoreProductsList({
      state: this.pagingState,
      api: this.api,
      pageSize: this.pageSize,
      toProduct: (raw) => new Product(raw),
    });
  }

  @action.bound
  openProduct(product: Product): void {
    openProductCard({
      product,
      store: this.store,
      navigationStore: this.navigationStore,
    });
  }

  resolveLocation = (product: Product): string => {
    return resolveProductLocation({
      product,
      store: this.store,
    });
  };

  @action.bound
  openTypeTag(tag: string): void {
    openTypeTagFilter({
      tag,
      navigationStore: this.navigationStore,
    });
  }

  @action.bound
  openMeasureFilter(measure: Measures): void {
    openMeasureFilter({
      measure,
      navigationStore: this.navigationStore,
    });
  }

  @action.bound
  openPlacementFilter(placement: ProductPlacementFilter): void {
    openPlacementFilter({
      placement,
      navigationStore: this.navigationStore,
      openAllProductsList: () => this.openAllProductsList(),
    });
  }

  @action.bound
  openExpiryFilter(
    expiry: ProductExpiryFilter,
    expiringDays: number = this.expiringDays,
  ): void {
    openExpiryFilter({
      expiry,
      expiringDays,
      navigationStore: this.navigationStore,
      openAllProductsList: () => this.openAllProductsList(),
    });
  }

  @action.bound
  openQueryFilter(query: string): void {
    openQueryFilter({
      query,
      navigationStore: this.navigationStore,
      openAllProductsList: () => this.openAllProductsList(),
    });
  }

  @action.bound
  openAllProductsList(): void {
    openAllProductsList(this.navigationStore);
  }

  @action.bound
  openLocationFilter(product: Product): void {
    void openLocationFilter({
      state: this.navigationState,
      product,
      api: this.api,
      store: this.store,
      navigationStore: this.navigationStore,
    });
  }

  @action.bound
  openReferenceId(id: string): void {
    void openReferenceId({
      state: this.navigationState,
      id,
      api: this.api,
      store: this.store,
      navigationStore: this.navigationStore,
    });
  }

  private get filterState(): ProductsListFilterState {
    return this;
  }

  private get pagingState(): ProductsListPagingState {
    return this;
  }

  private get navigationState(): ProductsListNavigationState {
    return this;
  }
}
